import * as React from 'react';

import { match } from 'path-to-regexp';

import { SettingsRootLayout } from './_components/_layouts/_SettingsRootLayout';
import { useSettingsLocation } from './_state/settingsModule.state';
import { MenuItem } from './_types';
import { SettingsPath, settingsRouteGroups } from './routes';

/**
 * Unlike other modules, the settings module is rendered in a dialog.
 * Hence, we use a custom router that handles the pathname in a query string parameter.
 */
// eslint-disable-next-line custom/require-component-classname
const SettingsRouter = () => {
  const [location, setLocation] = useSettingsLocation();

  const flattendedRoutes = React.useMemo(
    () => settingsRouteGroups.flatMap((group) => group.items as MenuItem[]),
    [],
  );

  const CurrentComponent = React.useMemo(() => {
    if (!location) return () => null;

    const exactMatch = flattendedRoutes.find((route) =>
      Array.isArray(route.pattern)
        ? route.pattern.includes(location)
        : route.pattern === location,
    )?.component;

    if (exactMatch) {
      return exactMatch;
    }

    const partialMatch = flattendedRoutes.find((route) => {
      if (Array.isArray(route.pattern)) {
        return route.pattern.some((pattern) => {
          const result = match(pattern)(location);
          return !!result;
        });
      }

      const result = match(route.pattern)(location);

      return !!result;
    })?.component;

    if (partialMatch) {
      return partialMatch;
    }
  }, [flattendedRoutes, location]);

  React.useEffect(() => {
    if (location === '/') {
      setLocation(
        flattendedRoutes.find((route) => !route.disabled)
          ?.pattern as SettingsPath,
      );
    }
  }, [flattendedRoutes, setLocation, location]);

  if (!location) {
    return null;
  }

  return (
    <SettingsRootLayout>
      {CurrentComponent && <CurrentComponent />}
    </SettingsRootLayout>
  );
};

export const settingsModule = {
  Router: SettingsRouter,
};
